import React, { useState, useEffect } from 'react';
import Button from '../ui/button';
import Input from '../ui/input';
import { X, Plus, Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import modelComparisonService from '../../services/modelComparisonService';

const ModelComparisonForm = ({ comparison, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    models: [
      { provider: 'openai', model: 'gpt-4' },
      { provider: 'anthropic', model: 'claude-3-opus' }
    ],
    template: '',
    metrics: ['accuracy', 'latency', 'cost'],
    parameters: {}
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [customModelMode, setCustomModelMode] = useState({});

  const providers = modelComparisonService.getProviders();
  const metrics = modelComparisonService.getMetrics();
  const templates = modelComparisonService.getTemplates();

  useEffect(() => {
    if (comparison) {
      setFormData({
        name: comparison.name || '',
        description: comparison.description || '',
        models: comparison.models || [
          { provider: 'openai', model: 'gpt-4' },
          { provider: 'anthropic', model: 'claude-3-opus' }
        ],
        template: comparison.template || '',
        metrics: comparison.metrics || ['accuracy', 'latency', 'cost'],
        parameters: comparison.parameters || {}
      });
    }
  }, [comparison]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    // Validation
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (formData.models.length < 2) {
      newErrors.models = 'At least 2 models are required for comparison';
    }
    if (formData.models.length > 10) {
      newErrors.models = 'Maximum 10 models allowed';
    }

    // Validate each model
    formData.models.forEach((model, index) => {
      if (!model.provider) {
        newErrors[`model_${index}_provider`] = 'Provider is required';
      }
      if (!model.model.trim()) {
        newErrors[`model_${index}_model`] = 'Model name is required';
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setLoading(false);
      return;
    }

    try {
      await onSave(formData);
    } catch (error) {
      if (error.errors) {
        setErrors(error.errors);
      }
    } finally {
      setLoading(false);
    }
  };

  const addModel = () => {
    if (formData.models.length >= 10) {
      toast.error('Maximum 10 models allowed');
      return;
    }
    setFormData(prev => ({
      ...prev,
      models: [...prev.models, { provider: 'openai', model: '' }]
    }));
  };

  const removeModel = (index) => {
    if (formData.models.length <= 2) {
      toast.error('At least 2 models are required');
      return;
    }
    setFormData(prev => ({
      ...prev,
      models: prev.models.filter((_, i) => i !== index)
    }));
  };

  const updateModel = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      models: prev.models.map((model, i) =>
        i === index ? { ...model, [field]: value } : model
      )
    }));
  };

  const toggleMetric = (metricValue) => {
    setFormData(prev => ({
      ...prev,
      metrics: prev.metrics.includes(metricValue)
        ? prev.metrics.filter(m => m !== metricValue)
        : [...prev.metrics, metricValue]
    }));
  };

  const getModelsForProvider = (provider) => {
    const providerData = providers.find(p => p.value === provider);
    return providerData ? providerData.models : [];
  };

  const isCustomModel = (index, provider, modelName) => {
    const providerModels = getModelsForProvider(provider);
    if (!providerModels || providerModels.length === 0) return true;
    if (customModelMode[index] === true) return true;
    return !!modelName && !providerModels.includes(modelName);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b flex items-center justify-between">
          <h2 className="text-xl font-semibold">
            {comparison ? 'Edit Model Comparison' : 'Create Model Comparison'}
          </h2>
          <Button variant="ghost" size="icon" onClick={onCancel}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Basic Information</h3>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Comparison Name *
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., GPT-4 vs Claude-3 Coding Test"
                  className={errors.name ? 'border-red-500' : ''}
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe what this comparison will evaluate..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Models Configuration */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium">Models to Compare</h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addModel}
                  disabled={formData.models.length >= 10}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Model
                </Button>
              </div>

              {errors.models && (
                <p className="text-red-500 text-sm">{errors.models}</p>
              )}

              <div className="space-y-3">
                {formData.models.map((model, index) => (
                  <div key={index} className="flex gap-3 items-start p-3 border rounded-lg">
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Provider
                        </label>
                        <select
                          value={model.provider}
                          onChange={(e) => {
                            updateModel(index, 'provider', e.target.value);
                            const selectedProvider = e.target.value;
                            const providerModels = getModelsForProvider(selectedProvider);
                            const shouldUseCustom = !providerModels || providerModels.length === 0; // only force custom if no options
                            setCustomModelMode(prev => ({ ...prev, [index]: shouldUseCustom }));
                            // Reset model when provider changes
                            if (!shouldUseCustom && providerModels.length > 0) {
                              updateModel(index, 'model', providerModels[0]);
                            } else {
                              updateModel(index, 'model', '');
                            }
                          }}
                          className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors[`model_${index}_provider`] ? 'border-red-500' : ''
                          }`}
                        >
                          {providers.map(provider => (
                            <option key={provider.value} value={provider.value}>
                              {provider.label}
                            </option>
                          ))}
                        </select>
                        {errors[`model_${index}_provider`] && (
                          <p className="text-red-500 text-xs mt-1">
                            {errors[`model_${index}_provider`]}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Model
                        </label>
                        {isCustomModel(index, model.provider, model.model) ? (
                          <div className="space-y-1">
                            <Input
                              value={model.model}
                              onChange={(e) => updateModel(index, 'model', e.target.value)}
                              placeholder="Enter model name (e.g., fine-tuned model)"
                              className={errors[`model_${index}_model`] ? 'border-red-500' : ''}
                            />
                            <div className="flex items-center justify-between">
                              <p className="text-xs text-gray-500">Enter any valid model identifier for the selected provider</p>
                              <button
                                type="button"
                                className="text-xs text-blue-600 hover:underline"
                                onClick={() => setCustomModelMode(prev => ({ ...prev, [index]: false }))}
                              >
                                Back to presets
                              </button>
                            </div>
                          </div>
                        ) : (
                          <select
                            value={model.model}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '__custom__') {
                                setCustomModelMode(prev => ({ ...prev, [index]: true }));
                                updateModel(index, 'model', '');
                              } else {
                                setCustomModelMode(prev => ({ ...prev, [index]: false }));
                                updateModel(index, 'model', value);
                              }
                            }}
                            className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              errors[`model_${index}_model`] ? 'border-red-500' : ''
                            }`}
                          >
                            {getModelsForProvider(model.provider).map(modelName => (
                              <option key={modelName} value={modelName}>
                                {modelName}
                              </option>
                            ))}
                            <option value="__custom__">Custom (enter manually)...</option>
                          </select>
                        )}
                        {errors[`model_${index}_model`] && (
                          <p className="text-red-500 text-xs mt-1">
                            {errors[`model_${index}_model`]}
                          </p>
                        )}
                      </div>
                    </div>

                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeModel(index)}
                      disabled={formData.models.length <= 2}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Template Selection */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Template</h3>
              <div>
                <select
                  value={formData.template}
                  onChange={(e) => setFormData(prev => ({ ...prev, template: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a template (optional)</option>
                  {templates.map(template => (
                    <option key={template.value} value={template.value}>
                      {template.label}
                    </option>
                  ))}
                </select>
                {formData.template && (
                  <p className="text-sm text-gray-600 mt-1">
                    {templates.find(t => t.value === formData.template)?.description}
                  </p>
                )}
              </div>
            </div>

            {/* Metrics Selection */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Metrics to Evaluate</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {metrics.map(metric => (
                  <label key={metric.value} className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.metrics.includes(metric.value)}
                      onChange={() => toggleMetric(metric.value)}
                      className="mt-1"
                    />
                    <div>
                      <div className="font-medium">{metric.label}</div>
                      <div className="text-sm text-gray-600">{metric.description}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t flex justify-end space-x-3">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : (comparison ? 'Update Comparison' : 'Create Comparison')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ModelComparisonForm;
